import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  Skeleton,
  Icon,
  Container,
  HStack,
  Divider,
} from "@chakra-ui/react";
import { 
  AggregationType,
  aggregationTypeFromJSON,
  aggregationTypeToJSON,
  ColumnType,
  columnTypeFromJSON,
  columnTypeToJSON,
  TimeBucket,
  timeBucketFromJSON,
  timeBucketToJSON,
} from "build-ts/enums";
import { KGColumnProto } from "build-ts/knowledege_graph";
import { getKGColumnById, updateKGColumn } from "services/DataModelService";
import showToast from "hooks/useCustomToast";
import { FaCog, FaDatabase } from "react-icons/fa";

interface EditKGColumnComponentProps {
  id: string | undefined;
  onClose: () => void;
  onSave?: () => void;

  // For saving the KG Column
  triggerSubmit?: boolean;
  setTriggerSubmit?: (value: boolean) => void;
  onReloadKnowledgeGraph?: () => void;
}

const EditKGColumnComponent: React.FC<EditKGColumnComponentProps> = ({
  id,
  onClose,
  onSave,
  triggerSubmit,
  setTriggerSubmit,
  onReloadKnowledgeGraph,
}) => {
  const [selectedColumn, setSelectedColumn] = useState<KGColumnProto | null>(null);
  const [columnType, setColumnType] = useState<ColumnType>(ColumnType.ATTRIBUTE);
  const [aggregation, setAggregation] = useState<AggregationType>(AggregationType.SUM);
  const [timeBucket, setTimeBucket] = useState<TimeBucket>(TimeBucket.DAILY);
  const [cardinality, setCardinality] = useState<number | undefined>(undefined);
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleKGColumn = async (id: string) => {
    if (!id) {
      setSelectedColumn(null);
      return;
    }
    setIsLoading(true);
    const kgColumn = await getKGColumnById(id, true);
    if (kgColumn) {
      setSelectedColumn(kgColumn);
      setName(kgColumn.header?.name || "");
      setDescription(kgColumn.header?.description || "");
      setColumnType(kgColumn.column_type || ColumnType.ATTRIBUTE);
      setAggregation(kgColumn.aggregation || AggregationType.SUM);
      setTimeBucket(kgColumn.time_bucket || TimeBucket.DAILY);
      setCardinality(kgColumn.cardinality);
    }
    setIsLoading(false);
  };

  const handleSave = async () => {
    if (!selectedColumn) return;
    if (!name.trim()) {
      showToast("Name is required", "", "error");
      return;
    }
    // Create updated header with new name/description
    const updatedHeader = {
      ...selectedColumn.header,
      name,
      description,
    };

    const updatedKgColumn: KGColumnProto = {
      ...selectedColumn,
      header: updatedHeader,
      column_type: columnType,
    };

    // Add properties based on column type
    if (columnType === ColumnType.ATTRIBUTE && cardinality !== undefined) {
      updatedKgColumn.cardinality = cardinality;
      // Remove any measure-specific properties
      delete updatedKgColumn.aggregation;
      delete updatedKgColumn.time_bucket;
    } else if (columnType === ColumnType.MEASURE) {
      updatedKgColumn.aggregation = aggregation;
      // Remove any attribute-specific properties
      delete updatedKgColumn.cardinality;
      delete updatedKgColumn.time_bucket;
    } else if (columnType === ColumnType.TEMPORAL) {
      updatedKgColumn.time_bucket = timeBucket;
      // Remove any other type-specific properties
      delete updatedKgColumn.aggregation;
      delete updatedKgColumn.cardinality;
    }

    const result = await updateKGColumn(updatedKgColumn, "",[], [], false);

    if (result) {
      showToast("KG Column updated successfully", "", "success");
      onSave?.();
      onClose(); // Close modal on successful save
      // Trigger reload of Knowledge Graph
      if (onReloadKnowledgeGraph) {
        onReloadKnowledgeGraph();
      }
    }
  };

  useEffect(() => {
    if (id) {
      handleKGColumn(id);
    }
  }, [id]);

  // Watch for triggerSubmit and call submit when triggered
  useEffect(() => {
    if (triggerSubmit) {
      handleSave();
      // Reset the trigger after submitting
      if (setTriggerSubmit) {
        setTriggerSubmit(false);
      }
    }
  },[triggerSubmit]);

  if (isLoading) {
    return (
      <VStack spacing={4} align="stretch">
        <Skeleton height="40px" />
        <Skeleton height="40px" />
        <Skeleton height="40px" />
        <Skeleton height="100px" />
      </VStack>
    );
  }

  if (!selectedColumn) {
    return (
      <Box
        p={10}
        textAlign="center"
        borderWidth="1px"
        borderRadius="md"
        bg="gray.50"
      >
        <Text color="gray.500" fontSize="lg">
          {id ? "Loading column details..." : "No column selected"}
        </Text>
      </Box>
    );
  }

  return (
    <Container maxW="container.xl" overflowY="auto" py={2}>
      <VStack spacing={8} align="stretch">
        {/* Column Properties Section */}
        <Box
          border="2px"
          borderColor="purple.700"
          borderRadius="xl"
          bg="white"
          boxShadow="lg"
        >
          {/* Header */}
          <HStack spacing={3} p={2} bg="purple.700" borderTopRadius="md">
            <Icon as={FaCog} boxSize={6} color="white" />
            <Text fontSize="lg" fontWeight="700" mb={0} color="white">
              
              {selectedColumn?.column_type === ColumnType.MEASURE ? "Measure Properties" : selectedColumn?.column_type === ColumnType.TEMPORAL ? "Temporal Properties" : "Attribute Properties"}
            </Text>
          </HStack>

          <VStack spacing={0} p={4}>
            {/* Name and Description */}
            <VStack spacing={4} width="100%">
              <FormControl flex="1" isRequired>
                <FormLabel fontWeight="600" color="gray.700" fontSize="sm">Name</FormLabel>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fontSize="sm"
                  bg="white"
                  borderColor="purple.700"
                  _focus={{ borderColor: "purple.700", bg: "white" }}
                />
              </FormControl>
              <FormControl flex="1">
                <FormLabel fontWeight="600" color="gray.700" fontSize="sm">Description</FormLabel>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fontSize="sm"
                  bg="white"
                  borderColor="purple.700"
                  _focus={{ borderColor: "purple.700", bg: "white" }}
                  size="sm"
                />
              </FormControl>
            </VStack>

            <Divider />

            {/* Column Type */}
            <HStack spacing={4} width="100%">
              <FormControl flex="1">
                <FormLabel fontWeight="600" color="gray.700" fontSize="sm">Column Type</FormLabel>
                <Select
                  value={columnTypeToJSON(columnType)}
                  onChange={(e) =>
                    setColumnType(columnTypeFromJSON(e.target.value))
                  }
                  fontSize="sm"
                  bg="white"
                  borderColor="purple.700"
                  _focus={{ borderColor: "purple.700", bg: "white" }}
                  isDisabled={true}
                >
                  <option value={columnTypeToJSON(ColumnType.ATTRIBUTE)}>
                    ATTRIBUTE
                  </option>
                  <option value={columnTypeToJSON(ColumnType.MEASURE)}>
                    MEASURE
                  </option>
                  <option value={columnTypeToJSON(ColumnType.TEMPORAL)}>
                    TEMPORAL
                  </option>
                </Select>
              </FormControl>
              {/* Type-specific properties */}
              {columnType === ColumnType.ATTRIBUTE && (
                <FormControl flex="1">
                  <FormLabel fontWeight="600" color="gray.700" fontSize="sm">Cardinality</FormLabel>
                  <NumberInput min={0} value={cardinality ?? ""}>
                    <NumberInputField
                      placeholder="Enter cardinality"
                      onChange={(e) =>
                        setCardinality(
                          e.target.value ? Number(e.target.value) : undefined
                        )
                      }
                      fontSize="sm"
                      bg="white"
                      borderColor="purple.700"
                      _focus={{ borderColor: "purple.700", bg: "white" }}
                    />
                  </NumberInput>
                </FormControl>
              )}
              {columnType === ColumnType.MEASURE && (
                <FormControl flex="1">
                  <FormLabel fontWeight="600" color="gray.700" fontSize="sm">Default Aggregation</FormLabel>
                  <Select
                    value={aggregationTypeToJSON(aggregation)}
                    onChange={(e) =>
                      setAggregation(aggregationTypeFromJSON(e.target.value))
                    }
                    fontSize="sm"
                    bg="white"
                    borderColor="purple.700"
                    _focus={{ borderColor: "purple.700", bg: "white" }}
                  >
                    <option
                      value={aggregationTypeToJSON(AggregationType.SUM)}
                    >
                      SUM
                    </option>
                    <option
                      value={aggregationTypeToJSON(AggregationType.AVERAGE)}
                    >
                      AVERAGE
                    </option>
                    <option
                      value={aggregationTypeToJSON(AggregationType.COUNT)}
                    >
                      COUNT
                    </option>
                    <option
                      value={aggregationTypeToJSON(
                        AggregationType.UNIQUE_COUNT
                      )}
                    >
                      UNIQUE_COUNT
                    </option>
                    <option
                      value={aggregationTypeToJSON(AggregationType.GROWTH)}
                    >
                      GROWTH
                    </option>
                    <option
                      value={aggregationTypeToJSON(AggregationType.MIN)}
                    >
                      MIN
                    </option>
                    <option
                      value={aggregationTypeToJSON(AggregationType.MAX)}
                    >
                      MAX
                    </option>
                  </Select>
                </FormControl>
              )}
              {columnType === ColumnType.TEMPORAL && (
                <FormControl flex="1">
                  <FormLabel fontWeight="600" color="gray.700" fontSize="sm">Time Bucket</FormLabel>
                  <Select
                    value={timeBucketToJSON(timeBucket)}
                    onChange={(e) =>
                      setTimeBucket(timeBucketFromJSON(e.target.value))
                    }
                    fontSize="sm"
                    bg="white"
                    borderColor="purple.700"
                    _focus={{ borderColor: "purple.700", bg: "white" }}
                  >
                    <option value={timeBucketToJSON(TimeBucket.DAILY)}>
                      DAILY
                    </option>
                    <option value={timeBucketToJSON(TimeBucket.WEEKLY)}>
                      WEEKLY
                    </option>
                    <option value={timeBucketToJSON(TimeBucket.MONTHLY)}>
                      MONTHLY
                    </option>
                    <option value={timeBucketToJSON(TimeBucket.QUARTERLY)}>
                      QUARTERLY
                    </option>
                    <option value={timeBucketToJSON(TimeBucket.YEARLY)}>
                      YEARLY
                    </option>
                  </Select>
                </FormControl>
              )}
            </HStack>
          </VStack>
        </Box>
        
        {/* Logical Mappings Section */}
        {selectedColumn?.logical_columns && selectedColumn?.logical_columns.length > 0 && (
          <Box
            border="2px"
            borderColor="green.600"
            borderRadius="xl"
            bg="white"
            boxShadow="lg"
          >
            {/* Header */}
            <HStack spacing={3} p={2} bg="green.600" borderTopRadius="md">
              <Icon as={FaDatabase} boxSize={6} color="white" />
              <Text fontSize="lg" fontWeight="700" mb={0} color="white">
                Logical Mappings
              </Text>
            </HStack>
            
            <Box p={6}>
              <Table
                variant="simple"
                size="sm"
                bg="white"
                borderRadius="md"
                overflow="hidden"
              >
                <Thead bg="gray.100">
                  <Tr>
                    <Th>Model ID</Th>
                    <Th>Logical Column</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {selectedColumn?.logical_columns?.map((column, index) => (
                    <Tr key={index}>
                      <Td fontSize="xs" color="gray.600">
                        {column?.owner_id}
                      </Td>
                      <Td fontWeight="medium">
                        {column?.name}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Note: Logical mappings cannot be modified through this interface.
              </Text>
            </Box>
          </Box>
        )}
      </VStack>
      
      {/* Footer with buttons */}
      {/* <Flex justify="flex-end" gap={3} mt={6} pt={4} borderTop="1px solid" borderColor="gray.200">
        <Button variant="outline" onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
        <Button
          colorScheme="purple"
          onClick={handleSave}
          isLoading={isSaving}
          isDisabled={!selectedColumn}
        >
          Save Changes
        </Button>
      </Flex> */}
    </Container>
  );
};

export default EditKGColumnComponent;